import { Progress } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InputMask from 'react-input-mask'
import { useNavigate } from 'react-router-dom'
import { SelectInstance } from 'react-select'

import { useStore } from 'store/useStore'
import { CountrySelectWithFlags } from 'sections/PromoSection/pages/PromoPage/shared/OpenPickPointForm/components/customSelect/CountrySelectWithFlags'
import { countrySelectCustomStyles } from 'sections/PromoSection/pages/PromoPage/shared/OpenPickPointForm/countrySelectCustomStyles'
import { PromoPageKeys } from 'sections/PromoSection/pages/PromoPage/types'
import { promoColors } from 'sections/PromoSection/shared/constants'
import { masks, phonePrefix } from 'components/CountryPhoneInput/data'
import { ButtonPromo, InputPromo, Margin, TextPromo } from 'components/ui'
import { Spinner } from 'components/ui/Spinner/Spinner'
import { Button, Flex } from 'components/uiKit'
import { formatPhoneNumberForText } from 'utils/formatting/formatPhoneNumber/formatPhomeNumberForText/formatPhoneNumberForText'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { useResize } from 'utils/hooks/useResize'
import { ALL_EXCEPT_NUMBERS_REGEXP } from 'shared/constants/regExp'
import { AuthRoutesEnum, LKRoutesEnum, PromoRoutesEnum } from 'shared/constants/routes'
import { URLS } from 'shared/constants/urls'
import { colors } from 'shared/constants/values'
import { BaseOption, Country } from 'shared/types/types'

import { countries } from '../../data'
import { LoginStatus } from '../../stores/LoginUiStore'
import { CountryItem, UserModuleKeys } from '../../types'

import ArrowIcon from 'assets/icons/arrow.svg?react'
import ArrowWhiteIcon from 'assets/icons/arrow-white.svg?react'
import desktopBackground from 'assets/img/login-page/login-page-background-desktop.webp'
import mobileBackground from 'assets/img/login-page/login-page-background-mobile.webp'
import tabletBackground from 'assets/img/login-page/login-page-background-tablet.webp'
import notificationPreviewDesktop from 'assets/img/login-page/login-page-notification-preview-desktop.webp'
import notificationPreviewMobile from 'assets/img/login-page/login-page-notification-preview-mobile.webp'
import notificationPreviewTablet from 'assets/img/login-page/login-page-notification-preview-tablet.webp'
import sparksDesktop from 'assets/img/login-page/login-page-sparks-desktop.webp'
import sparksMobile from 'assets/img/login-page/login-page-sparks-mobile.webp'
import sparksTablet from 'assets/img/login-page/login-page-sparks-tablet.webp'
import sparksVideoTablet from 'assets/img/login-page/login-page-sparks-video-tablet.webp'
import phoneDesktop from 'assets/img/login-page/phone-desktop.webp'
import notificationInfoVideo from 'assets/videos/notification_info.mp4'
import s from './LoginPage.module.scss'

const CODE_LENGTH = 6

export const LoginPage = observer(() => {
  const { t } = useTranslation()
  const { isTablet, isMobile, isSTablet, isSDesktop, isDesktop } = useResize()
  const { loginUiStore: uiStore, appStore } = useStore()
  const [sixDigitsCode, setSixDigitsCode] = useState(['', '', '', '', '', ''])
  const [videoProgress, setVideoProgress] = useState(0)
  const [isVisibleVideoModal, setVisibleVideoModal] = useState(false)

  const inputRefs = useRef<HTMLInputElement[]>([])
  const selectRef = useRef<SelectInstance<any, boolean> | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const phoneInputRef = useRef<InputMask>(null)
  const localizedCountries = useMemo(() => {
    countries.sort((a, b) => a.label.localeCompare(b.label))
    return countries.map((country) => ({
      ...country,
      label: t(getSharedCountryKey(country.value.label)),
    }))
  }, [t])
  const [selectedCountry, setSelectedCountry] = useState<BaseOption<CountryItem> | null>(
    isGeDomain()
      ? localizedCountries.find((country) => country.value.country === 'GE') || localizedCountries[5]
      : localizedCountries[5],
  )
  const [menuPlacement, setMenuPlacement] = useState<'top' | 'bottom'>('bottom')

  const { timeIsNotOver, timeExpired } = uiStore.timeWaiter
  const navigate = useNavigate()
  const getKeyPromo = (key: string) => `${PromoPageKeys.openPickPointForm}.${key}`
  const getKey = (key: string) => `${UserModuleKeys.loginPage}.${key}`

  const countryKey = selectedCountry?.value?.country as keyof typeof masks

  const backgroundUrl = getBackgroundUrl()
  const layoutStyles = {
    backgroundImage: `url(${backgroundUrl})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  }

  const previewUrl = getPreviewUrl()
  const previewStyles = {
    backgroundImage: `url(${previewUrl})`,
  }

  const sparksUrl = getSparksUrl()
  const sparksStyles = {
    backgroundImage: `url(${sparksUrl})`,
  }

  useEffect(() => {
    if (!isMobile && !isTablet) {
      setVisibleVideoModal(false)
    }
  }, [isMobile, isTablet])

  useEffect(() => {
    if (uiStore.isValidateVisible) {
      inputRefs.current[0].focus()
    }
  }, [uiStore.isValidateVisible])

  useEffect(() => {
    const player = videoRef.current

    player?.addEventListener('timeupdate', () => {
      const time = Math.round(
        ((videoRef?.current?.currentTime ?? 0) * 100) / (videoRef.current?.duration ? videoRef.current?.duration : 1),
      )
      setVideoProgress(time)
    })

    return () => {
      player?.removeEventListener('timeupdate', () => {})
    }
  }, [isVisibleVideoModal])

  useEffect(() => {
    if (sixDigitsCode.join('').length === 6) {
      handleSetCode()
    }
  }, [sixDigitsCode])

  useEffect(() => {
    if (!localizedCountries.length) return

    const computedDefaultOption = isGeDomain()
      ? localizedCountries.find((country) => country.value.country === 'GE') || localizedCountries[5]
      : localizedCountries[5]

    setSelectedCountry(computedDefaultOption)

    uiStore.setSelectedCountry(computedDefaultOption.value)
  }, [localizedCountries, uiStore])

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight
      const rect = selectRef.current?.controlRef?.getBoundingClientRect()

      if (rect) {
        const spaceBelow = windowHeight - rect.bottom
        const spaceAbove = rect.top

        if (spaceBelow < 300 && spaceAbove > 300) {
          setMenuPlacement('top')
        } else {
          setMenuPlacement('bottom')
        }
      }
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [menuPlacement])

  const handleInputChange = (index: number, value: string) => {
    const digit = value.replace(ALL_EXCEPT_NUMBERS_REGEXP, '')
    const newCode = [...sixDigitsCode]
    newCode[index] = digit
    setSixDigitsCode(newCode)

    if (digit.length === 1) {
      const nextInput = inputRefs.current[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      const newCode = [...sixDigitsCode]

      if (!newCode[index] && index > 0) {
        const previousInput = inputRefs.current[index - 1]
        if (previousInput) {
          previousInput.focus()
        }
      }
    }
  }

  const handleSetCode = async () => {
    uiStore.setCode(sixDigitsCode.join(''))

    if (sixDigitsCode.length !== CODE_LENGTH) {
      return
    }

    const result = await uiStore.validateCode()

    if (result) {
      await bootstrap().finally(() => {
        uiStore.clearTimer()
        uiStore.setCode('')
        uiStore.setDefaultState()
        setSixDigitsCode(['', '', '', '', '', ''])
      })
    }
  }

  const handlePressBtn = async () => {
    const result = await uiStore.tryLogin(false)
    if (result === LoginStatus.validateCode) {
      bootstrap()
    } else if (result === LoginStatus.loggedIn) {
      uiStore.setValidateVisible(true)
    }
  }

  const bootstrap = async () => {
    await appStore.bootstrap()
    const screen = appStore.needRegistration ? AuthRoutesEnum.registration : LKRoutesEnum.orders
    appStore.setLoadingApp(false)
    navigate(screen, { replace: true })
  }

  const handleCountryChange = (item: BaseOption<CountryItem>) => {
    setSelectedCountry(item)
    uiStore.setPhone('')
    uiStore.setSelectedCountry(item.value)
  }

  const setDefaultState = () => {
    uiStore.setDefaultState()
    uiStore.setSelectedCountry(selectedCountry?.value)
    uiStore.setPhone('')
    setSixDigitsCode(['', '', '', '', '', ''])
  }

  const handleButtonBack = () => {
    if (uiStore.isValidateVisible) {
      setDefaultState()
    } else {
      navigate(PromoRoutesEnum.root, { replace: true })
    }
  }

  function getBackgroundUrl() {
    if (isMobile) {
      return mobileBackground
    }
    if (isTablet) {
      return tabletBackground
    }
    return desktopBackground
  }

  function getPreviewUrl() {
    if (isMobile) {
      return uiStore.isValidateVisible ? sparksMobile : notificationPreviewMobile
    }
    if (isTablet) {
      return uiStore.isValidateVisible ? sparksTablet : notificationPreviewTablet
    }
    return notificationPreviewDesktop
  }

  function getSparksUrl() {
    if (isMobile) {
      return sparksMobile
    }
    return sparksVideoTablet
  }

  function getSharedCountryKey(key: string) {
    return `${PromoPageKeys.countries}.${key}`
  }

  return (
    <Flex className={s.LoginPage} vertical={isMobile || isTablet} flex={1} style={layoutStyles}>
      <Flex flex={1} style={{ position: 'relative' }}>
        {(isMobile || isTablet) && <div className={s.notificationPreview} style={previewStyles} />}

        {!isMobile && !isTablet && (
          <div className={s.videoDesktopWrapper} style={{ backgroundImage: `url(${sparksDesktop})` }}>
            {uiStore.isValidateVisible ? (
              <video className={s.videoDesktop} autoPlay loop muted>
                <source src={notificationInfoVideo} />
              </video>
            ) : (
              <div style={{ position: 'relative' }}>
                <div className={s.phoneImage} style={{ backgroundImage: `url(${phoneDesktop})` }} />
              </div>
            )}
          </div>
        )}
      </Flex>
      <Flex
        className={s.form}
        vertical
        style={{
          height: uiStore.isValidateVisible
            ? isMobile || isTablet
              ? '90vh'
              : 'auto'
            : isMobile
              ? '74vh'
              : isTablet
                ? '67vh'
                : 'auto',
        }}
      >
        {appStore.isLoadingUserInfo && (
          <div className={s.activityIndicator}>
            <Spinner size={100} />
          </div>
        )}
        <Button
          className={s.buttonBack}
          icon={isMobile || isTablet ? <ArrowWhiteIcon className={s.caret} /> : <ArrowIcon className={s.caret} />}
          color={'default'}
          iconPosition="start"
          variant={'text'}
          onClick={handleButtonBack}
        >
          {uiStore.isValidateVisible ? t(getSharedKey('back')) : t(getKey('Вернуться на сайт'))}
        </Button>
        <TextPromo
          size={isSTablet ? 's32' : 's60'}
          style={{ textAlign: isTablet || isMobile ? 'center' : 'left' }}
          fontFamily="Unbounded"
          weight="w700"
        >
          {!uiStore.isValidateVisible ? t(getKey('Регистрация и вход')) : t(getKey('Введите код'))}
        </TextPromo>
        <Margin
          mb={uiStore.isValidateVisible ? (isMobile ? 16 : isTablet ? 32 : 48) : isMobile ? 16 : isTablet ? 32 : 56}
          mt={uiStore.isValidateVisible ? (isMobile ? 16 : 32) : isMobile ? 8 : 32}
        >
          <Flex flex={1} vertical>
            <TextPromo
              size={isMobile ? 's16' : 's24'}
              fontFamily="ASLHauss"
              style={{ textAlign: isMobile || isTablet ? 'center' : 'left' }}
            >
              {uiStore.isValidateVisible ? (
                <>
                  {t(getKeyPromo('Отправили код в профиль Wildberries'), { count: Number(uiStore.phonePrefix) })}{' '}
                  {formatPhoneNumberForText(uiStore.phone)}
                </>
              ) : (
                t(getKey('Введите телефон'))
              )}
            </TextPromo>
          </Flex>
        </Margin>

        {!uiStore.isValidateVisible ? (
          <>
            <Flex gap={isMobile ? 24 : 16} vertical={isMobile} align="center" style={{ marginBottom: 'auto' }}>
              <Flex className={s.inputWrapper} align="center">
                <CountrySelectWithFlags
                  onChange={(item) => handleCountryChange(item as BaseOption<CountryItem>)}
                  menuPosition="absolute"
                  styles={countrySelectCustomStyles(
                    { menu: { margin: isSDesktop || isDesktop ? 5 : 0, 'margin-top': isMobile ? -5 : 5 } },
                    menuPlacement,
                  )}
                  className={s.customSelect}
                  value={selectedCountry}
                  options={localizedCountries}
                  isDisabled={isGeDomain()}
                  isSearchable={false}
                  components={{}}
                  ref={selectRef}
                />
                <Flex
                  align="center"
                  flex={1}
                  style={{ cursor: 'pointer', height: '100%' }}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  onClick={() => phoneInputRef.current?.focus()}
                >
                  <Flex>
                    <span className={s.countryCode}>{`+${phonePrefix[countryKey] ?? '+7'}`}</span>
                  </Flex>
                  <InputMask
                    value={uiStore.phone}
                    onChange={({ target: { value } }) => uiStore.setPhone(value)}
                    type="tel"
                    inputMode="numeric"
                    mask={masks[countryKey] ?? masks[Country.RU]}
                    maskPlaceholder={null}
                    className={s.input}
                    ref={phoneInputRef}
                  />
                </Flex>
              </Flex>
              <ButtonPromo
                className={s.loginButton}
                fullWidth={isMobile}
                onClick={uiStore.allowLogin ? handlePressBtn : undefined}
                variant={isMobile ? 'purple' : 'arrow'}
                disabled={!uiStore.allowLogin}
              >
                <TextPromo pointer fontFamily="ASLHaussVF" size="s20" color="white">
                  {t(getSharedKey('next'))}
                </TextPromo>
              </ButtonPromo>
            </Flex>

            <Margin mt={50}>
              <Flex vertical>
                <TextPromo
                  center
                  fontFamily="ASLHauss"
                  size={isMobile ? 's12' : 's16'}
                  weight="w400"
                  style={{ textAlign: isMobile || isTablet ? 'center' : 'left' }}
                >
                  {t(getKeyPromo('Нажимая «Далее»'), { nbsp: '\u00A0' })}{' '}
                  <a
                    href={isGeDomain() ? URLS.offerGE : URLS.offer}
                    target="_blank"
                    style={{
                      fontWeight: 400,
                      color: promoColors.newBgPrimary,
                      textDecoration: 'none',
                      fontSize: isMobile ? 12 : 16,
                    }}
                    rel="noreferrer"
                  >
                    {t(getKeyPromo('офертой'))}
                  </a>{' '}
                  {t(getKeyPromo('и'))}{' '}
                  <a
                    href={isGeDomain() ? URLS.wbMarketPrivatePolicyGe : URLS.wbMarketPrivatePolicy}
                    target="_blank"
                    style={{
                      fontWeight: 400,
                      color: promoColors.newBgPrimary,
                      textDecoration: 'none',
                      fontSize: isMobile ? 12 : 16,
                    }}
                    rel="noreferrer"
                  >
                    {t(getKeyPromo('политикой конфиденциальности'))}
                  </a>
                </TextPromo>
              </Flex>
            </Margin>
          </>
        ) : (
          <>
            <Flex gap={isMobile ? 12 : 16}>
              {sixDigitsCode.map((digit, index) => (
                <InputPromo
                  className={s.customInput}
                  key={index}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  onFocus={() => {
                    const parentElement = inputRefs.current[index]?.closest(`.${s.customInput}`)
                    if (parentElement) {
                      parentElement.setAttribute('data-active', 'true')
                    }
                  }}
                  onBlur={(e) => {
                    const parentElement = e.target.closest(`.${s.customInput}`)
                    if (parentElement) {
                      parentElement.removeAttribute('data-active')
                    }
                  }}
                  inputRef={(ref) => {
                    inputRefs.current[index] = ref as HTMLInputElement
                  }}
                />
              ))}
            </Flex>

            <Flex flex={1} vertical justify={isMobile || isTablet ? 'start' : 'end'}>
              <Margin mt={isMobile ? 32 : isTablet ? 48 : 0}>
                {(isTablet || isMobile) && (
                  <ButtonPromo
                    onClick={() => setVisibleVideoModal(true)}
                    variant="outlined"
                    fullWidth
                    className={s.button}
                  >
                    <TextPromo pointer fontFamily="ASLHaussVF" size="s20" color="primary">
                      {t(getKey('Где посмотреть код?'))}
                    </TextPromo>
                  </ButtonPromo>
                )}
                {timeIsNotOver ? (
                  <Flex vertical align={isMobile || isTablet ? 'center' : 'start'}>
                    <Margin mt={isMobile || isTablet ? 23 : 0}>
                      <TextPromo
                        fontFamily="ASLHaussVF"
                        size="s16"
                        weight="w400"
                        style={{ textAlign: isMobile || isTablet ? 'center' : 'left', width: '100%' }}
                      >
                        {t(getKey('Отправить код ещё раз через'), {
                          time: timeExpired,
                        })}
                      </TextPromo>
                    </Margin>
                  </Flex>
                ) : (
                  <Margin mt={16}>
                    <ButtonPromo className={s.button} variant={'link'} fullWidth onClick={() => uiStore.tryLogin(true)}>
                      <TextPromo pointer fontFamily="ASLHaussVF" size="s20" color="primary">
                        {t(getKey('Отправить код еще раз'))}
                      </TextPromo>
                    </ButtonPromo>
                  </Margin>
                )}
              </Margin>
            </Flex>
          </>
        )}
      </Flex>

      {isVisibleVideoModal && (
        <div className={s.videoModal} style={layoutStyles}>
          <Button
            className={s.buttonBack}
            icon={<ArrowIcon className={s.caret} />}
            color={'default'}
            iconPosition="start"
            variant={'text'}
            onClick={() => setVisibleVideoModal(false)}
          >
            {t(getSharedKey('back'))}
          </Button>
          <Margin mt={isMobile ? 119 : 109} mb={52}>
            <Flex vertical>
              <TextPromo
                size={isMobile ? 's28' : 's48'}
                style={{ textAlign: 'center', zIndex: 1 }}
                fontFamily="Unbounded"
                color={'white'}
                weight="w700"
              >
                {t(getKey('Где посмотреть код'))}
              </TextPromo>
            </Flex>
          </Margin>
          <Flex vertical align="center">
            <div className={s.modalSparks} style={sparksStyles} />
            <video ref={videoRef} className={s.modalPlayer} autoPlay loop muted>
              <source src={notificationInfoVideo} />
            </video>

            <Progress
              percent={videoProgress}
              className={s.progress}
              showInfo={false}
              strokeColor={colors.white}
              trailColor={colors.whiteLight}
            />
          </Flex>
          <ButtonPromo className={s.closeModal} onClick={() => setVisibleVideoModal(false)} variant={'arrow'} />
        </div>
      )}
    </Flex>
  )
})
